import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { isNative, openAppSettings } from '@/native/permissions';
import {
  disablePushNotifications,
  enablePushNotifications,
} from '@/native/pushNotifications';
import {
  disableWebPush,
  enableWebPush,
  isWebPushSupported,
} from '@/native/webPush';

/**
 * Hook for the Settings / onboarding "Enable notifications" affordance.
 *
 * On native (iOS/Android via Capacitor) → uses /native/pushNotifications.js.
 * In a real browser → uses /native/webPush.js (Firebase Web SDK + SW + VAPID).
 *
 * Returns `{ state, enable, disable }` where state is:
 *   'unsupported'  — neither native nor a browser that supports web push
 *   'idle'         — not yet asked
 *   'requesting'   — system dialog is up
 *   'enabled'      — registered with backend
 *   'denied'       — user said no; toast with "Open Settings" already shown
 *   'error'        — something blew up; toast already shown
 */
export function usePushNotifications() {
  const navigate = useNavigate();
  const [state, setState] = useState(() => {
    if (isNative()) return 'idle';
    if (isWebPushSupported()) return 'idle';
    return 'unsupported';
  });

  const handleIncoming = useCallback(
    (event) => {
      if (event.kind === 'received') {
        const n = event.notification || {};
        toast(n.title || 'New notification', { description: n.body });
      } else if (event.kind === 'action') {
        const link = event.action?.notification?.data?.deep_link;
        if (link && link.startsWith('/')) {
          navigate(link);
        }
      }
    },
    [navigate]
  );

  const enable = useCallback(async () => {
    setState('requesting');
    const result = isNative()
      ? await enablePushNotifications({ onIncoming: handleIncoming })
      : await enableWebPush({ onIncoming: handleIncoming });

    if (result.ok) {
      setState('enabled');
      toast.success("You're subscribed to updates.");
      return true;
    }
    if (result.reason === 'denied') {
      setState('denied');
      toast.error('Notifications are turned off for TheUnityBlaze.', {
        description: isNative()
          ? 'Open Settings to enable them.'
          : 'Enable notifications for this site in your browser settings.',
        action: isNative()
          ? { label: 'Open Settings', onClick: () => openAppSettings() }
          : undefined,
      });
      return false;
    }
    if (result.reason === 'unsupported') {
      setState('unsupported');
      return false;
    }
    setState('error');
    toast.error('Could not enable notifications. Try again later.');
    return false;
  }, [handleIncoming]);

  const disable = useCallback(async () => {
    if (isNative()) {
      await disablePushNotifications();
    } else {
      await disableWebPush();
    }
    setState('idle');
  }, []);

  // Auto-resume listeners on app launch when permission is already granted.
  // We don't auto-request — only auto-resume.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isNative()) {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          const perm = await PushNotifications.checkPermissions();
          if (!cancelled && perm.receive === 'granted') {
            await enablePushNotifications({ onIncoming: handleIncoming });
            setState('enabled');
          }
        } else if (isWebPushSupported() && Notification.permission === 'granted') {
          const result = await enableWebPush({ onIncoming: handleIncoming });
          if (!cancelled && result.ok) setState('enabled');
        }
      } catch (e) {
        // No-op — auto-resume is best-effort.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handleIncoming]);

  return { state, enable, disable };
}
